import { Platform } from "react-native";
import {
  isTopUpProductId,
  productCredits,
  type PurchaseFulfillResponse,
  type TopUpProductId,
} from "@nativecall/shared";
import { purchaseFulfillResponseSchema } from "@nativecall/shared";

let configuredUserId: string | null = null;

function isNative(): boolean {
  return Platform.OS === "ios" || Platform.OS === "android";
}

function getApiKey(): string | null {
  const key = process.env.EXPO_PUBLIC_REVENUECAT_KEY;
  return key && key.length > 0 ? key : null;
}

async function loadPurchases() {
  if (!isNative()) {
    return null;
  }
  const mod = await import("react-native-purchases");
  return mod.default ?? mod;
}

export async function configurePurchases(userId: string): Promise<void> {
  if (configuredUserId === userId) {
    return;
  }
  const Purchases = await loadPurchases();
  if (!Purchases) {
    return;
  }
  const apiKey = getApiKey();
  if (!apiKey) {
    return;
  }
  if (configuredUserId === null) {
    Purchases.configure({ apiKey, appUserID: userId });
  } else {
    await Purchases.logIn(userId);
  }
  configuredUserId = userId;
}

export async function signOutPurchases(): Promise<void> {
  const Purchases = await loadPurchases();
  if (!Purchases) {
    configuredUserId = null;
    return;
  }
  if (configuredUserId !== null) {
    try {
      await Purchases.logOut();
    } catch {
      // ignore; logOut throws if the user is anonymous
    }
  }
  configuredUserId = null;
}

export type TopUpPackage = {
  productId: TopUpProductId;
  credits: number;
  priceString: string;
  packageIdentifier: string;
  title: string;
  description: string;
};

export async function fetchTopUpPackages(): Promise<TopUpPackage[]> {
  const Purchases = await loadPurchases();
  if (!Purchases) {
    return [];
  }
  const offerings = await Purchases.getOfferings();
  const current = offerings.current;
  if (!current) {
    return [];
  }
  const mapped: TopUpPackage[] = [];
  for (const pkg of current.availablePackages) {
    const productId = pkg.product.identifier;
    if (!isTopUpProductId(productId)) {
      continue;
    }
    mapped.push({
      productId,
      credits: productCredits[productId],
      priceString: pkg.product.priceString,
      packageIdentifier: pkg.identifier,
      title: pkg.product.title,
      description: pkg.product.description,
    });
  }
  // Present packages sorted by ascending credit amount so users see the
  // smallest pack first and can step up.
  return mapped.sort((a, b) => a.credits - b.credits);
}

export type PurchaseOutcome =
  | {
      kind: "success";
      productId: TopUpProductId;
      transactionId: string;
    }
  | {
      kind: "cancelled";
    };

export async function purchaseTopUpPackage(
  packageIdentifier: string,
): Promise<PurchaseOutcome> {
  const Purchases = await loadPurchases();
  if (!Purchases) {
    throw new Error("Purchases are only supported on iOS and Android.");
  }
  const offerings = await Purchases.getOfferings();
  const current = offerings.current;
  if (!current) {
    throw new Error("No RevenueCat offering is currently active.");
  }
  const pkg = current.availablePackages.find(
    (candidate) => candidate.identifier === packageIdentifier,
  );
  if (!pkg) {
    throw new Error("Selected top-up pack is no longer available.");
  }

  try {
    const result = await Purchases.purchasePackage(pkg);
    const productId = result.productIdentifier;
    if (!isTopUpProductId(productId)) {
      throw new Error(`Unknown product id returned from purchase: ${productId}`);
    }
    const transactionId =
      result.transaction?.transactionIdentifier ??
      result.customerInfo?.originalAppUserId ??
      productId;
    return { kind: "success", productId, transactionId };
  } catch (error) {
    if (
      error &&
      typeof error === "object" &&
      (error as { userCancelled?: boolean }).userCancelled
    ) {
      return { kind: "cancelled" };
    }
    throw error;
  }
}

export async function restorePurchases(): Promise<TopUpProductId[]> {
  const Purchases = await loadPurchases();
  if (!Purchases) {
    return [];
  }
  const customerInfo = await Purchases.restorePurchases();
  const productIds: TopUpProductId[] = [];
  for (const id of customerInfo.allPurchasedProductIdentifiers ?? []) {
    if (isTopUpProductId(id)) {
      productIds.push(id);
    }
  }
  return productIds;
}

export function parseFulfillResponse(value: unknown): PurchaseFulfillResponse {
  return purchaseFulfillResponseSchema.parse(value);
}
